import { passkeyClient } from '@better-auth/passkey/client';
import { magicLinkClient, organizationClient, phoneNumberClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { clearEmbeddedSessionToken, getEmbeddedSessionToken } from './embedded-auth';

// Auth client connects to existing Better Auth backend
export const authClient = createAuthClient({
  baseURL:
    import.meta.env.VITE_API_URL ||
    (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8787'),
  // Embedded side-panel iframe: send the Bearer session token so useSession() +
  // every auth call work without the (unavailable cross-site) session cookie. ''
  // for normal web, where better-auth skips the header and uses the cookie.
  fetchOptions: {
    auth: {
      type: 'Bearer',
      token: () => getEmbeddedSessionToken(),
    },
  },
  plugins: [
    organizationClient(),
    magicLinkClient(),
    phoneNumberClient(),
    // Wires authClient.signIn.passkey() + authClient.passkey.addPasskey() etc.
    // The WebAuthn ceremonies run in the browser; the gateway-side @better-
    // auth/passkey plugin (auth/index.tsx) verifies the assertions. Local
    // single-user mode uses signIn.passkey for the biometric sign-in path;
    // multi-user deployments expose the same surface in /settings → Passkeys.
    passkeyClient(),
  ],
});

// Export commonly used methods. `useActiveOrganization` / `useListOrganizations`
// are intentionally NOT re-exported: their XHRs (`get-full-organization`, `list`)
// were on the shell's critical path and duplicated data already returned by
// `/api/organizations`. The UI derives both from `useOrgContext` instead.
// `organization.setActive` / `.create` / `.update` / `.delete` / `.listMembers`
// remain for ceremony flows.
export const { signIn, signUp, useSession, organization, phoneNumber } = authClient;

// Wrap signOut so the embedded panel also drops its Bearer token — otherwise it
// could keep authenticating as the signed-out user until the iframe remounts.
export const signOut: typeof authClient.signOut = (...args) => {
  clearEmbeddedSessionToken();
  return authClient.signOut(...args);
};

// Type exports
export type Session = typeof authClient.$Infer.Session;
export type Organization = typeof authClient.$Infer.Organization;
